<template>
  <v-navigation-drawer
    v-if="shouldShowSidebar && isMobile"
    location="top"
    permanent
    width="100%"
    :model-value="true"
    color="primary"
    class="mobile-top-drawer"
    style="height: 49px; z-index: 1004"
  >
    <div
      v-if="!concentrationModeActive"
      class="d-flex align-center justify-start mobile-top-drawer__content"
    >
      <v-btn
        icon
        color="white"
        variant="text"
        class="mr-1 flex-shrink-0"
        data-tour="nav-menu"
        aria-label="Abrir menú"
        @click="drawer = true"
      >
        <v-icon>mdi-menu</v-icon>
      </v-btn>
      <v-btn
        v-if="mobileProjectTitle"
        icon
        color="white"
        variant="text"
        size="small"
        density="comfortable"
        class="mobile-top-drawer__back flex-shrink-0"
        aria-label="Volver a Mis proyectos"
        @click="$router.push('/projects')"
      >
        <v-icon size="18">mdi-arrow-left</v-icon>
      </v-btn>
      <span
        v-if="mobileProjectTitle"
        class="mobile-top-drawer__project-title"
        :title="mobileProjectTitle"
      >
        {{ mobileProjectTitle }}
      </span>
      <AppNavbarActions />
    </div>
  </v-navigation-drawer>

  <div
    v-if="shouldShowSidebar && isMobile && !concentrationModeActive"
    data-tour="nav-menu-anchor"
    class="tour-nav-menu-anchor"
    aria-hidden="true"
  />
  <div
    v-if="shouldShowSidebar && isMobile && !concentrationModeActive"
    data-tour="sidebar-card-anchor"
    class="tour-sidebar-card-anchor"
    aria-hidden="true"
  />

  <v-navigation-drawer
    v-if="shouldShowSidebar && !concentrationModeActive"
    v-model="drawer"
    :rail="rail && !isHovering"
    :permanent="!isMobile"
    :temporary="isMobile"
    disable-route-watcher
    class="sidebar"
    elevation="2"
    width="280"
    fixed
    @mouseenter="onSidebarMouseEnter"
    @mouseleave="onSidebarMouseLeave"
  >
    <div
      class="sidebar-header"
      data-tour="nav-account"
      role="button"
      :aria-label="isGuestUser ? 'Iniciar sesión' : 'Ir al perfil'"
      @click="onAccountClick"
    >
      <div class="sidebar-account d-flex align-center pa-3">
        <AvatarAsset
          v-if="avatarSignedUrl"
          :signed-url="avatarSignedUrl"
          :size="32"
          :class="{ 'mr-3': !rail || isHovering }"
        />
        <v-avatar
          v-else
          size="32"
          color="primary"
          :class="{ 'mr-3': !rail || isHovering }"
        >
          <v-icon>mdi-account-circle</v-icon>
        </v-avatar>
        <div v-if="!rail || isHovering" class="sidebar-user-info">
          <div class="text-subtitle-1 font-weight-medium text-grey-darken-1">
            {{ userDisplayName }}
          </div>
          <div class="text-caption text-grey-darken-1">
            {{ userTypeLabel }}
          </div>
        </div>
        <v-icon
          v-if="!rail || isHovering"
          class="sidebar-account__settings"
          size="18"
          aria-hidden="true"
        >
          mdi-cog-outline
        </v-icon>
      </div>
    </div>

    <v-divider />

    <AppDomainSwitcher
      v-if="showDomainSwitcher"
      :collapsed="isSidebarCollapsed"
    />

    <v-divider v-if="showDomainSwitcher" />

    <v-list
      v-model:opened="openedGroups"
      nav
      class="sidebar-nav"
      :class="{ 'sidebar-nav--collapsed': isSidebarCollapsed }"
    >
      <template v-for="item in navigationItems" :key="item.value">
        <div
          v-if="item.kind === 'section'"
          :data-tour="`nav-${item.value}`"
        >
          <hr
            v-if="isSidebarCollapsed"
            class="sidebar-section-divider"
          >
          <div
            v-else
            class="sidebar-section"
          >
            {{ item.title }}
          </div>
        </div>

        <v-list-group
          v-else-if="item.children"
          :value="item.value"
          :fluid="isSidebarCollapsed"
          :class="{
            'sidebar-group--active': isSidebarCollapsed && isActiveNavigationGroup(item.value),
          }"
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              :prepend-icon="item.icon"
              :title="item.title"
              class="sidebar-item"
              :data-tour="`nav-${item.value}`"
            />
          </template>

          <v-list-item
            v-for="child in item.children"
            :key="child.value"
            :to="child.action ? undefined : child.to"
            :prepend-icon="child.icon"
            :title="child.title"
            :value="child.value"
            color="primary"
            class="sidebar-item sidebar-child-item"
            :class="{
              'sidebar-child-item--active': childMatchesRoute(child),
              'sidebar-child-item--hidden': isSidebarCollapsed && !childMatchesRoute(child),
            }"
            :data-tour="`nav-${child.value}`"
            @click="onNavItemClick(child, $event)"
          />
        </v-list-group>

        <v-list-item
          v-else
          :to="item.action ? undefined : item.to"
          :active="navItemActive(item)"
          :title="item.title"
          :value="item.value"
          color="primary"
          class="sidebar-item"
          :data-tour="`nav-${item.value}`"
          @click="onNavItemClick(item, $event)"
        >
          <template #prepend>
            <v-badge
              v-if="item.badge"
              :content="item.badge > 99 ? '99+' : item.badge"
              color="error"
              floating
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>
            <v-icon v-else>{{ item.icon }}</v-icon>
          </template>
        </v-list-item>
      </template>
    </v-list>

    <template v-if="isOnline || !isGuestUser" #append>
      <template v-if="isOnline">
        <v-divider />
        <v-list nav class="sidebar-nav sidebar-nav--fixed" :class="{ 'sidebar-nav--collapsed': isSidebarCollapsed }">
          <v-list-item
            v-for="item in fixedNavigationItems"
            :key="item.value"
            :to="item.action ? undefined : item.to"
            :active="navItemActive(item)"
            :title="item.title"
            :value="item.value"
            color="primary"
            class="sidebar-item"
            :data-tour="`nav-${item.value}`"
            @click="onNavItemClick(item, $event)"
          >
            <template #prepend>
              <v-icon>{{ item.icon }}</v-icon>
            </template>
          </v-list-item>
        </v-list>
        <router-link
          v-if="!isSidebarCollapsed"
          class="sidebar-terms"
          to="/terms-and-conditions"
          @click="closeNavigationPanel"
        >
          Términos y Condiciones
        </router-link>
      </template>
      <div v-if="!isGuestUser" class="sidebar-footer">
        <v-list-item
          prepend-icon="mdi-logout"
          title="Cerrar sesión"
          class="sidebar-item logout-item"
          @click="logout"
        />
      </div>
    </template>
  </v-navigation-drawer>

  <div
    v-if="rail && isHovering && !isMobile && shouldShowSidebar && !concentrationModeActive"
    class="sidebar-hover-overlay"
    @click="isHovering = false"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useAuthStore } from '@/stores/auth'
import { useUserProfileStore } from '@/stores/userProfile'
import { useConcentrationModeStore } from '@/stores/concentrationMode'
import { useGroupChatsStore } from '@/stores/groupChats'
import { useHelpChatStore } from '@/stores/helpChat'
import { useNotificationsStore } from '@/stores/notifications'
import { useProjectMobileHeaderStore } from '@/stores/projectMobileHeader'
import AvatarAsset from '@/components/assets/AvatarAsset.vue'
import AppNavbarActions from '@/components/layout/AppNavbarActions.vue'
import AppDomainSwitcher from '@/components/layout/AppDomainSwitcher.vue'
import { useAppDomainStore } from '@/stores/appDomain'
import {
  buildAppFixedNavigationItems,
  buildAppNavigationItems,
  navigationItemMatchesRoute,
  shouldShowAppSidebar,
  type AppNavigationItem,
} from './appNavigation'
import { useConnectionStatus } from '@/composables/useConnectionStatus'
import { useOfflineLibrary } from '@/composables/useOfflineLibrary'

export default defineComponent({
  name: 'AppSidebar',

  components: {
    AvatarAsset,
    AppNavbarActions,
    AppDomainSwitcher,
  },

  setup() {
    const isMobile = useDisplay().mobile
    const concentrationModeStore = useConcentrationModeStore()
    const { active: concentrationModeActive } = storeToRefs(concentrationModeStore)
    const helpChatStore = useHelpChatStore()
    const groupChatsStore = useGroupChatsStore()
    const notificationsStore = useNotificationsStore()
    const appDomainStore = useAppDomainStore()
    const projectMobileHeaderStore = useProjectMobileHeaderStore()
    const { title: mobileProjectTitle } = storeToRefs(projectMobileHeaderStore)
    const authStore = useAuthStore()
    const { isOnline } = useConnectionStatus()
    const { deviceProjects, refreshDeviceProjects } = useOfflineLibrary()

    return {
      isMobile,
      concentrationModeActive,
      helpChatStore,
      groupChatsStore,
      notificationsStore,
      appDomainStore,
      mobileProjectTitle,
      isOnline,
      deviceProjects,
      refreshDeviceProjects,
    }
  },

  data() {
    return {
      drawer: true,
      rail: true,
      isHovering: false,
      ignoreSidebarHover: false,
      openedGroups: [] as string[],
      authStore: useAuthStore(),
      userProfileStore: useUserProfileStore(),
    }
  },

  computed: {
    shouldShowSidebar(): boolean {
      return shouldShowAppSidebar(
        this.authStore.userType,
        this.$route.name,
        this.$route.meta.publicMarketing,
      )
    },

    userDisplayName(): string {
      if (this.authStore.userType === 'guest') {
        return 'Invitado'
      }
      return this.authStore.currentUser?.name || 'Usuario'
    },

    userTypeLabel(): string {
      const userType = this.authStore.userType
      if (userType === 'guest') {
        return 'Usuario invitado'
      }
      if (userType === 'registered') {
        return 'Usuario registrado'
      }
      return 'No autenticado'
    },

    isGuestUser(): boolean {
      return this.authStore.userType === 'guest'
    },

    avatarSignedUrl(): string {
      return this.userProfileStore.avatarSignedUrl
    },

    isSidebarCollapsed(): boolean {
      return !this.isMobile && this.rail && !this.isHovering
    },

    showDomainSwitcher(): boolean {
      return !this.isGuestUser && this.isOnline
    },

    fixedNavigationItems(): AppNavigationItem[] {
      if (this.authStore.userType === 'non-authenticated' || !this.isOnline) {
        return []
      }

      return buildAppFixedNavigationItems({
        isGuest: this.isGuestUser,
      })
    },

    navigationItems(): AppNavigationItem[] {
      if (this.authStore.userType === 'non-authenticated') {
        return []
      }

      if (!this.isOnline) {
        const manageCopies = {
          title: 'En este dispositivo',
          icon: 'mdi-cellphone-arrow-down',
          to: '/local-copies',
          value: 'local-copies',
          show: true,
        }

        if (this.deviceProjects.length === 0) {
          return [
            {
              title: 'Sin copias locales',
              icon: 'mdi-wifi-off',
              to: '/projects',
              value: 'offline-empty',
              show: true,
            },
            manageCopies,
          ]
        }

        return [...this.deviceProjects.map((project) => {
          const value = `offline-${project.projectId}`
          const chapters = project.chapters.map((chapter) => ({
            title: chapter.title,
            icon: 'mdi-text-box-outline',
            to: `/project/${project.projectId}?tab=editor&chapterId=${chapter.chapterId}`,
            value: `offline-chapter-${chapter.chapterId}`,
            show: true,
          }))

          if (chapters.length === 0) {
            return {
              title: project.title,
              icon: 'mdi-book-open-page-variant',
              to: `/project/${project.projectId}?tab=editor`,
              value,
              show: true,
            }
          }

          return {
            title: project.title,
            icon: 'mdi-book-open-page-variant',
            value,
            show: true,
            children: chapters,
          }
        }), manageCopies]
      }

      return buildAppNavigationItems({
        isGuest: this.isGuestUser,
        domain: this.appDomainStore.selected,
      })
    },
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.syncOpenedGroupsToActiveRoute()
    this.syncDomainFromRoute()
    void this.refreshDeviceProjects()
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },

  methods: {
    checkMobile() {
      if (this.isMobile) {
        this.drawer = false
        this.rail = false
        this.isHovering = false
      } else {
        this.drawer = true
        this.rail = true
        this.isHovering = false
      }
    },

    async logout() {
      if (!this.isGuestUser) {
        const didLogout = await this.authStore.logout()
        if (!didLogout) {
          return
        }
        this.userProfileStore.clear()
      }
      this.notificationsStore.reset()
      window.location.href = '/'
    },

    onSidebarMouseEnter() {
      if (this.ignoreSidebarHover || !(this.rail && !this.isMobile)) {
        return
      }
      this.isHovering = true
    },

    onSidebarMouseLeave(event: MouseEvent) {
      if (!(this.rail && !this.isMobile)) {
        return
      }
      const { clientX, clientY } = event
      window.requestAnimationFrame(() => {
        const underPointer = document.elementFromPoint(clientX, clientY)
        const sidebar = document.querySelector('.sidebar')
        if (
          underPointer
          && sidebar instanceof HTMLElement
          && sidebar.contains(underPointer)
        ) {
          return
        }
        this.ignoreSidebarHover = false
        this.isHovering = false
      })
    },

    openOfflineProjectGroups() {
      this.openedGroups = this.navigationItems
        .filter((item) => item.children && item.children.length > 0)
        .map((item) => item.value)
    },

    closeNavigationPanel() {
      if (this.isMobile) {
        this.drawer = false
        return
      }
      this.ignoreSidebarHover = true
      this.isHovering = false
    },

    onAccountClick() {
      this.closeNavigationPanel()
      if (this.isGuestUser) {
        this.$router.push('/login')
      } else {
        this.$router.push('/account')
      }
    },

    onNavItemClick(item: { action?: string; to?: string }, event: Event) {
      this.closeNavigationPanel()
      if (item.action === 'help') {
        event.preventDefault()
        event.stopPropagation()
        this.groupChatsStore.close()
        this.notificationsStore.closePreview()
        this.helpChatStore.open()
      }
    },

    syncDomainFromRoute() {
      this.appDomainStore.syncFromRoute({
        routeDomain: this.$route.meta.domain,
        isGuest: this.isGuestUser,
      })
    },

    navItemActive(item: AppNavigationItem): boolean {
      return navigationItemMatchesRoute(item, this.$route)
    },

    childMatchesRoute(child: AppNavigationItem): boolean {
      return navigationItemMatchesRoute(child, this.$route)
    },

    getActiveNavigationGroup(): string | null {
      for (const item of this.navigationItems) {
        if (!item.children) {
          continue
        }
        if (item.children.some((child) => this.childMatchesRoute(child))) {
          return item.value
        }
      }
      return null
    },

    isActiveNavigationGroup(groupValue: string): boolean {
      return this.getActiveNavigationGroup() === groupValue
    },

    syncOpenedGroupsToActiveRoute() {
      const activeGroup = this.getActiveNavigationGroup()
      this.openedGroups = activeGroup ? [activeGroup] : []
    },

  },

  watch: {
    isOnline(online: boolean) {
      void this.refreshDeviceProjects()
      if (online) {
        this.syncOpenedGroupsToActiveRoute()
      }
    },
    deviceProjects() {
      if (!this.isOnline) {
        this.openOfflineProjectGroups()
      }
    },
    '$route.fullPath'() {
      if (!this.isOnline) {
        void this.refreshDeviceProjects()
      }
    },
    isHovering(isHovering: boolean) {
      if (!isHovering && this.rail && !this.isMobile) {
        this.syncOpenedGroupsToActiveRoute()
      }
    },
    drawer(isOpen: boolean) {
      if (!isOpen) {
        this.syncOpenedGroupsToActiveRoute()
      }
    },
    openedGroups(openedGroups: string[]) {
      if (!this.isSidebarCollapsed) {
        return
      }

      const activeGroup = this.getActiveNavigationGroup()
      const expected = activeGroup ? [activeGroup] : []
      const isInSync =
        openedGroups.length === expected.length &&
        openedGroups.every((value, index) => value === expected[index])

      if (!isInSync) {
        this.openedGroups = expected
      }
    },
    $route() {
      this.syncDomainFromRoute()

      const panelClosed = this.isMobile ? !this.drawer : this.rail && !this.isHovering
      if (panelClosed) {
        this.syncOpenedGroupsToActiveRoute()
      }
    },
  },
})
</script>

<style scoped>
.tour-nav-menu-anchor {
  position: fixed;
  top: 64px;
  left: 20px;
  width: 1px;
  height: 1px;
  pointer-events: none;
  z-index: 1001;
}

.tour-sidebar-card-anchor {
  position: fixed;
  left: 24px;
  bottom: 12px;
  width: 1px;
  height: 1px;
  pointer-events: none;
  z-index: 1001;
}

.sidebar {
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  position: fixed !important;
  z-index: 1002;
}

.sidebar.v-navigation-drawer--temporary {
  top: 49px !important;
  height: calc(100% - 49px) !important;
  max-height: calc(100% - 49px) !important;
}

.sidebar-header {
  background: rgb(var(--v-theme-surface-variant));
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  cursor: pointer;
}

.sidebar-account {
  width: 100%;
}

.sidebar-user-info {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.sidebar-user-info .text-subtitle-1,
.sidebar-user-info .text-caption {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-account__settings {
  flex-shrink: 0;
  margin-left: 8px;
  color: #616161;
}

.sidebar-nav {
  padding: 8px 0;
}

.sidebar-nav--fixed {
  padding-bottom: 0;
}

.sidebar-section {
  padding: 16px 20px 4px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  color: rgba(var(--v-theme-on-surface), 0.54);
}

.sidebar-section-divider {
  display: block;
  height: 1px;
  margin: 8px 16px;
  border: none;
  background: rgba(var(--v-theme-on-surface), 0.12);
}

.sidebar-terms {
  display: block;
  padding: 4px 20px 12px;
  font-size: 0.6875rem;
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.54);
  text-decoration: none;
}

.sidebar-terms:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}

.sidebar-item {
  margin: 4px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.sidebar-child-item {
  max-height: 56px;
  overflow: hidden;
  transition:
    opacity 0.28s ease,
    max-height 0.28s ease,
    margin 0.28s ease,
    padding 0.28s ease,
    min-height 0.28s ease,
    transform 0.28s ease;
}

.sidebar-child-item--hidden {
  opacity: 0;
  max-height: 0 !important;
  min-height: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  pointer-events: none;
  transform: translateY(-4px);
}

.sidebar-nav--collapsed :deep(.v-list-group__header .v-list-item__append) {
  display: none;
}

.sidebar-nav--collapsed :deep(.sidebar-group--active > .v-list-group__items) {
  --indent-padding: 0px;
}

.sidebar-nav--collapsed
  :deep(.sidebar-group--active > .v-list-group__items .sidebar-child-item--active) {
  opacity: 1;
  padding-inline-start: 16px !important;
  margin-inline-start: 8px !important;
  margin-inline-end: 8px !important;
}

.sidebar-nav--collapsed :deep(.v-list-group__header.sidebar-item) {
  pointer-events: none;
}

.sidebar-item:hover {
  background-color: rgba(33, 150, 243, 0.08);
}

.sidebar-item.logout-item {
  margin-left: 0 !important;
}

.sidebar-footer {
  padding: 8px 0;
}

.logout-item {
  color: #f44336;
}

.logout-item:hover {
  background-color: rgba(244, 67, 54, 0.08);
}

.sidebar-hover-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1001;
  cursor: pointer;
}

.mobile-top-drawer {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.mobile-top-drawer:deep(.v-navigation-drawer__content) {
  width: 100%;
}

.mobile-top-drawer__content {
  width: 100%;
  height: 100%;
  min-width: 0;
  padding-right: 4px;
}

.mobile-top-drawer__back {
  width: 28px;
  min-width: 28px;
  height: 28px;
}

.mobile-top-drawer__project-title {
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-top-drawer .v-btn {
  color: white !important;
}

.v-theme--dark .mobile-top-drawer {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.v-theme--light .mobile-top-drawer {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
